import Papa from 'papaparse';
import { v4 as uuid } from 'uuid';

import { definitions } from '@luxuryescapes/contract-svc-promo';

type VoucherReward = definitions['VoucherRewardV1'];
type CsvColumn =
  | 'id_voucher_reward'
  | 'reward_amount'
  | 'currency_code'
  | 'products'
  | 'min_spend'
  | 'min_spend_products';
type VoucherRewardCsvRow = {
  id_voucher_reward: string;
  reward_amount: number;
  currency_code?: string;
  products: Array<string>;
  min_spend?: number;
  min_spend_products?: Array<string>;
};

export const VOUCHER_REWARD_CSV_COLUMNS = [
  'id_voucher_reward',
  'reward_amount',
  'currency_code',
  'products',
  'min_spend',
  'min_spend_products',
] satisfies ReadonlyArray<CsvColumn>;
type CsvRow = Record<CsvColumn, string>;

export type VoucherRewardCsvWarning = {
  level: 'error';
  message: string;
};

export type VoucherRewardCsvCandidate = {
  action: 'create' | 'update';
  rowNumber: number;
  reward: VoucherReward;
  warnings: Array<VoucherRewardCsvWarning>;
};

export type ParseVoucherRewardCsvOptions = {
  existingRewards: Array<VoucherReward>;
  validCurrencies: Array<string>;
  validProducts: Array<string>;
  validMinSpendProducts: Array<string>;
};

export type VoucherRewardCsvImportResult = {
  candidates: Array<VoucherRewardCsvCandidate>;
  warnings: Array<VoucherRewardCsvWarning>;
};

/** Serialises every editable voucher reward field so the text can be safely pasted back into the import box. */
export function toCSV(rewards: Array<VoucherReward>): string {
  const fields: Array<CsvColumn> = [...VOUCHER_REWARD_CSV_COLUMNS];
  return Papa.unparse({
    fields,
    data: rewards.map(
      (reward): CsvRow => ({
        id_voucher_reward: reward.id_voucher_reward,
        reward_amount: String(reward.reward_amount),
        currency_code: reward.currency_code ?? '',
        products: JSON.stringify(reward.products ?? []),
        min_spend: reward.min_spend == null ? '' : String(reward.min_spend),
        min_spend_products: JSON.stringify(reward.min_spend_products ?? []),
      }),
    ),
  });
}

function parseProductArray(
  value: string | undefined,
  field: string,
  warnings: Array<VoucherRewardCsvWarning>,
): Array<string> {
  try {
    const parsed: unknown = JSON.parse(value || '[]');
    if (!Array.isArray(parsed) || !parsed.every((item) => typeof item === 'string')) {
      warnings.push({ level: 'error', message: `${field} must be a JSON array of strings` });
      return [];
    }
    return parsed;
  } catch {
    warnings.push({ level: 'error', message: `${field} must be valid JSON` });
    return [];
  }
}

function parseNonNegativeNumber(
  value: string | undefined,
  field: string,
  warnings: Array<VoucherRewardCsvWarning>,
  required = false,
): number | undefined {
  if (!value?.trim() && !required) {
    return undefined;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0 || (required && parsed === 0)) {
    warnings.push({
      level: 'error',
      message: `${field} must be${required ? ' a positive' : ' a non-negative'} number`,
    });
    return 0;
  }
  return parsed;
}

function isVoucherRewardCurrencyCode(value: string): value is VoucherReward['currency_code'] {
  return value.length > 0;
}

function isVoucherRewardProduct(value: string): value is VoucherReward['products'][number] {
  return value.length > 0;
}

function isVoucherRewardMinSpendProduct(
  value: string,
): value is NonNullable<VoucherReward['min_spend_products']>[number] {
  return value.length > 0;
}

export function parseVoucherRewardCSV(
  csv: string,
  options: ParseVoucherRewardCsvOptions,
): VoucherRewardCsvImportResult {
  const parsed = Papa.parse<CsvRow>(csv, { header: true, skipEmptyLines: 'greedy' });
  const warnings = parsed.errors.map(
    (error): VoucherRewardCsvWarning => ({
      level: 'error',
      message: `Row ${(error.row ?? 0) + 2}: ${error.message}`,
    }),
  );
  const missingColumns = VOUCHER_REWARD_CSV_COLUMNS.filter((column) => !(parsed.meta.fields ?? []).includes(column));
  if (missingColumns.length > 0) {
    warnings.push({ level: 'error', message: `Missing CSV columns: ${missingColumns.join(', ')}` });
  }

  const existingIds = new Set(options.existingRewards.map((reward) => reward.id_voucher_reward));
  const importedIds = new Set<string>();
  const candidates = parsed.data.map((row, index) => {
    const rowWarnings: Array<VoucherRewardCsvWarning> = [];
    const id = row.id_voucher_reward?.trim();
    const rewardId = id ?? uuid();
    const products = parseProductArray(row.products, 'products', rowWarnings);
    const minSpendProducts = parseProductArray(row.min_spend_products, 'min_spend_products', rowWarnings);
    const currency = row.currency_code?.trim().toUpperCase();

    if (id && !existingIds.has(id)) {
      rowWarnings.push({ level: 'error', message: 'id_voucher_reward does not match a row on this voucher' });
    }
    if (id && importedIds.has(id)) {
      rowWarnings.push({ level: 'error', message: 'id_voucher_reward appears more than once in this import' });
    }
    if (id) {
      importedIds.add(id);
    }
    if (!currency || !options.validCurrencies.includes(currency)) {
      rowWarnings.push({ level: 'error', message: 'currency_code is not a supported currency' });
    }
    if (products.length === 0) {
      rowWarnings.push({ level: 'error', message: 'products must include at least one product' });
    }
    products
      .filter((product) => !options.validProducts.includes(product))
      .forEach((product) => {
        rowWarnings.push({ level: 'error', message: `products includes unsupported value ${product}` });
      });
    minSpendProducts
      .filter((product) => !options.validMinSpendProducts.includes(product))
      .forEach((product) => {
        rowWarnings.push({ level: 'error', message: `min_spend_products includes unsupported value ${product}` });
      });

    const minSpend = parseNonNegativeNumber(row.min_spend, 'min_spend', rowWarnings);
    if (minSpend === undefined && minSpendProducts.length > 0) {
      rowWarnings.push({ level: 'error', message: 'min_spend is required when min_spend_products are supplied' });
    }

    const action: VoucherRewardCsvCandidate['action'] = id ? 'update' : 'create';
    const currencyCode =
      currency && isVoucherRewardCurrencyCode(currency)
        ? currency
        : (options.validCurrencies.find(isVoucherRewardCurrencyCode) ?? 'AUD');
    const typedProducts = products.filter(isVoucherRewardProduct);
    const typedMinSpendProducts = minSpendProducts.filter(isVoucherRewardMinSpendProduct);
    const reward: VoucherReward = {
      id_voucher_reward: rewardId,
      reward_amount: parseNonNegativeNumber(row.reward_amount, 'reward_amount', rowWarnings, true) ?? 0,
      currency_code: currencyCode,
      products: typedProducts,
      ...(minSpend === undefined ? {} : { min_spend: minSpend }),
      min_spend_products: typedMinSpendProducts,
    };

    return {
      action,
      rowNumber: index + 2,
      warnings: rowWarnings,
      reward,
    };
  });

  return { candidates, warnings };
}
